import {useEffect, useMemo, useRef, useState} from "react";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SettingsIcon from "@mui/icons-material/Settings";
import {Card, Grid, IconButton, Tooltip, Typography} from "@mui/material";
import _ from "lodash";
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  useMaterialReactTable
} from "material-react-table";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import ButtonSelect from "../../components/button-select";
import CsvIcon from "../../components/custom-icons/CsvIcon";
import FullscreenProvider, {useFullscreen} from "../../hocs/fullscreen-provider";
import {
  COLUMN_TYPES,
  CSV_EXPORT_MODES,
  DEFAULT_PAGE_SIZE,
  FLAT_TABLE_NO_INTERACTION_PROPS,
  FLAT_TABLE_PROPS,
  TABLE_CARD_STYLE,
  TABLE_LANGUAGES
} from "./constants";
import {
  AddressField,
  CoordinatesField,
  DateField,
  DayField,
  DimensionField,
  ImageField,
  MailField,
  MeasureField,
  MonthField,
  PhoneField,
  QuadrimesterField,
  QuarterField,
  SemesterField,
  TimeField,
  WebsiteField,
  YearField
} from "./Renderers";
import SettingsDialog from "./SettingsDialog";
import {appConfigSelector} from "../../state/app-config/appConfigSelectors";
import {languageSelector} from "../../state/app/appSelectors";
import {getFormattedValue} from "../../utils/formatters";
import "./style.css";

const FlatTableComponent = ({
  columns: externalColumns,
  rowCount,
  data,
  fetchData,
  initialState,
  initialSettings,
  title,
  columnOrder: externalColumnOrder,
  decimalPlaces,
  showTitle = false,
  disableSettings = false,
  disableInteractions = false,
  disableFilters = true, // TODO: to be removed
  disableSorting = false, // TODO: to be removed
  disableColumnOrdering = false,
  disableFullscreen = false,
  disableWheelZoom = false,
  nodeId,
  decimalSeparator,
  roundingStrategy,
  leftActions,
  rightActions,
  exportCsv,
  onConfigChange
}) => {
  const {t} = useTranslation();

  const {isFullscreen, handleFullscreen} = useFullscreen();

  const appConfig = useSelector(appConfigSelector);
  const language = useSelector(languageSelector);

  const tableContainerRef = useRef(null);

  // Memoization of table columns to prevent unnecessary table re-renders
  const columns = useMemo(() => {
    const columnList = externalColumns ? _.cloneDeep(externalColumns) : null;

    if (columnList) {
      const latitude = columnList.find(column => column.type === COLUMN_TYPES.lat);
      const longitude = columnList.find(column => column.type === COLUMN_TYPES.lon);
      if (latitude && longitude) {
        columnList.unshift({
          id: COLUMN_TYPES.coordinates,
          title: t("modules.flatTable.table.header.coordinates.label"),
          Header: <></>,
          type: COLUMN_TYPES.coordinates,
          latitude: latitude.id,
          longitude: longitude.id,
          enableSorting: false,
          enableColumnFilter: false,
          enableColumnDragging: false,
          enableColumnActions: false,
          size: 70,
          grow: false
        });
      }
    }

    return columnList
      ? columnList.map(column => ({
          ...column,
          accessorKey: column.id,
          header: column.title,
          Header: () => <Tooltip title={column.title}>{column.title}</Tooltip>,
          accessorFn: (row, index) => {
            switch (column.type) {
              case COLUMN_TYPES.measure:
                return (
                  <MeasureField
                    data={row[column.id]}
                    decimalPlaces={
                      column.decimals !== undefined && column.decimals !== null && column.decimals >= 0
                        ? column.decimals
                        : decimalPlaces
                    }
                    decimalSeparator={decimalSeparator}
                    roundingStrategy={roundingStrategy}
                    index={index}
                  />
                );
              case COLUMN_TYPES.addr:
                return <AddressField data={row[column.id]} index={index} />;
              case COLUMN_TYPES.website:
                return <WebsiteField data={row[column.id]} index={index} />;
              case COLUMN_TYPES.phone:
                return <PhoneField data={row[column.id]} index={index} />;
              case COLUMN_TYPES.mail:
                return <MailField data={row[column.id]} index={index} />;
              case COLUMN_TYPES.dateTime:
                return <DateField nodeId={nodeId} data={row[column.id]} index={index} />;
              case COLUMN_TYPES.time:
                return <TimeField nodeId={nodeId} data={row[column.id]} index={index} />;
              case COLUMN_TYPES.day:
                return <DayField nodeId={nodeId} data={row[column.id]} index={index} />;
              case COLUMN_TYPES.month:
                return <MonthField nodeId={nodeId} data={row[column.id]} index={index} />;
              case COLUMN_TYPES.year:
                return <YearField nodeId={nodeId} data={row[column.id]} index={index} />;
              case COLUMN_TYPES.quarter:
                return <QuarterField nodeId={nodeId} data={row[column.id]} index={index} />;
              case COLUMN_TYPES.quadrimester:
                return <QuadrimesterField nodeId={nodeId} data={row[column.id]} index={index} />;
              case COLUMN_TYPES.semester:
                return <SemesterField nodeId={nodeId} data={row[column.id]} index={index} />;
              case COLUMN_TYPES.image:
                return <ImageField data={row[column.id]} index={index} />;
              case COLUMN_TYPES.coordinates:
                return (
                  <CoordinatesField latitude={row[column.latitude]} longitude={row[column.longitude]} index={index} />
                );
              default:
                return <DimensionField data={row[column.id]} index={index} />;
            }
          }
        }))
      : null;
  }, [externalColumns, decimalPlaces, decimalSeparator, roundingStrategy, nodeId, t]);

  // Table state
  const [isLoading, setIsLoading] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(initialState?.columnVisibility || {});
  const [columnOrder, setColumnOrder] = useState(initialState?.columnOrder || []);
  const [columnPinning, setColumnPinning] = useState(initialState?.columnPinning || {});
  const [columnFilters, setColumnFilters] = useState(initialState?.columnFilters || []);
  const [globalFilter, setGlobalFilter] = useState(initialState?.globalFilter || "");
  const [sorting, setSorting] = useState(initialState?.sorting || []);
  const [pagination, setPagination] = useState(
    initialState?.pagination || {
      pageIndex: 0,
      pageSize: appConfig?.flatTableConfig?.pageSize || DEFAULT_PAGE_SIZE
    }
  );
  const [tableSettings, setTableSettings] = useState(initialSettings || {});
  const [isSettingsVisible, setSettingsVisibility] = useState(false);

  useEffect(() => {
    if (onConfigChange) {
      onConfigChange({
        tableState: {
          columnVisibility: columnVisibility,
          columnOrder: columnOrder,
          columnPinning: columnPinning
        },
        tableSettings: tableSettings
      });
    }
  }, [columnVisibility, columnOrder, columnPinning, tableSettings, onConfigChange]);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;

    if (!disableWheelZoom || !tableContainer) {
      return undefined;
    }

    const handleWheel = event => {
      event.preventDefault();

      window.scrollBy({
        top: event.deltaY,
        left: event.deltaX,
        behavior: "auto"
      });
    };

    tableContainer.addEventListener("wheel", handleWheel, {passive: false});

    return () => {
      tableContainer.removeEventListener("wheel", handleWheel);
    };
  }, [disableWheelZoom]);

  useEffect(() => {
    if (data && columns) {
      setIsLoading(false);
    } else {
      const pagination = initialState?.pagination || {
        pageIndex: 0,
        pageSize: appConfig?.flatTableConfig?.pageSize || DEFAULT_PAGE_SIZE
      };
      const sorting = initialState?.sorting || [];
      const columnFilters = initialState?.columnFilters || [];
      const globalFilter = initialState?.globalFilter || "";
      setIsLoading(true);
      fetchData(pagination, sorting, columnFilters, globalFilter, columns);
    }
  }, [data, columns, fetchData, initialState, appConfig]);

  const onCsvExport = mode => {
    const selectedColumns =
      mode === CSV_EXPORT_MODES.filtered
        ? columnOrder.filter(column => columnVisibility[column] !== false && column !== COLUMN_TYPES.coordinates)
        : null;
    exportCsv(mode, selectedColumns, sorting, columnFilters, globalFilter);
  };

  const table = useMaterialReactTable({
    localization: TABLE_LANGUAGES[language],
    columns: columns || [],
    rowCount: rowCount,
    data: isLoading ? [] : data || [],
    initialState: {
      density: "compact"
    },
    state: {
      isLoading: isLoading,
      columnVisibility: columnVisibility,
      columnOrder: [COLUMN_TYPES.coordinates, ...(externalColumnOrder ? externalColumnOrder : columnOrder)],
      columnPinning: columnPinning,
      columnFilters: columnFilters,
      globalFilter: globalFilter,
      sorting: sorting,
      pagination: pagination
    },
    enableGlobalFilter: false,
    enablePagination: pagination.pageSize < rowCount,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onColumnFiltersChange: updater => {
      const updatedColumnFilters = updater(columnFilters);
      setIsLoading(true);
      setColumnFilters(updatedColumnFilters);
      fetchData(pagination, sorting, updatedColumnFilters, globalFilter, columns);
    },
    onGlobalFilterChange: updater => {
      const updatedGlobalFilter = updater(globalFilter);
      setIsLoading(true);
      setGlobalFilter(updatedGlobalFilter);
      fetchData(pagination, sorting, columnFilters, updatedGlobalFilter, columns);
    },
    onSortingChange: updater => {
      const updatedSorting = updater(sorting);
      setIsLoading(true);
      setSorting(updatedSorting);
      fetchData(pagination, updatedSorting, columnFilters, globalFilter, columns);
    },
    onPaginationChange: updater => {
      const updatedPagination = updater(pagination);
      setIsLoading(true);
      setPagination(updatedPagination);
      fetchData(updatedPagination, sorting, columnFilters, globalFilter, columns);
    },
    renderTopToolbarCustomActions: () =>
      showTitle && (title || "").length > 0 ? (
        <Typography variant="h6" className="flat-table__toolbar__title">
          {title}
        </Typography>
      ) : (
        leftActions
      ),
    renderToolbarInternalActions: ({table}) => (
      <Grid container alignItems="center" wrap="nowrap">
        {exportCsv &&
          (disableInteractions ? (
            <Grid className={"flat-table__toolbar-actions__export flat-table__toolbar-actions__export--button"}>
              <IconButton
                aria-label={t("scenes.dataViewer.actions.exportCsv.tooltip")}
                onClick={() => onCsvExport(CSV_EXPORT_MODES.filtered)}
              >
                <Tooltip title={t("scenes.dataViewer.actions.exportCsv.tooltip")}>
                  <CsvIcon />
                </Tooltip>
              </IconButton>
            </Grid>
          ) : (
            <Grid className={"flat-table__toolbar-actions__export flat-table__toolbar-actions__export--select"}>
              <ButtonSelect
                className={"flat-table__toolbar-actions__export flat-table__toolbar-actions__export--select"}
                icon={<CsvIcon />}
                tooltip={t("scenes.dataViewer.actions.exportCsv.tooltip")}
                ariaLabel={t("scenes.dataViewer.actions.exportCsv.tooltip")}
                color="default"
                onChange={value => onCsvExport(value)}
              >
                <span data-value={CSV_EXPORT_MODES.full}>
                  {t("scenes.dataViewer.dialogs.exportCsv.export.values.full")}
                </span>
                <span data-value={CSV_EXPORT_MODES.filtered}>
                  {t("scenes.dataViewer.dialogs.exportCsv.export.values.filtered")}
                </span>
              </ButtonSelect>
            </Grid>
          ))}
        {!disableInteractions && !disableFilters && (
          <Grid className="flat-table__toolbar-actions__toggle-filters">
            <MRT_ToggleFiltersButton table={table} />
          </Grid>
        )}
        {!disableInteractions && (
          <Grid className="flat-table__toolbar-actions__show-hide-columns">
            <MRT_ShowHideColumnsButton table={table} />
          </Grid>
        )}
        {!disableSettings && (
          <Grid className={"flat-table__toolbar-actions__settings"}>
            <IconButton
              aria-label={t("modules.flatTable.actions.settings.tooltip")}
              onClick={() => setSettingsVisibility(true)}
            >
              <Tooltip title={t("modules.flatTable.actions.settings.tooltip")}>
                <SettingsIcon />
              </Tooltip>
            </IconButton>
          </Grid>
        )}
        {rightActions}
        {!disableFullscreen && (
          <Grid className="flat-table__toolbar-actions__fullscreen-btn">
            <Tooltip
              title={
                isFullscreen
                  ? t("modules.flatTable.actions.fullscreenBtn.exit")
                  : t("modules.flatTable.actions.fullscreenBtn.enter")
              }
              placement="bottom"
            >
              <IconButton onClick={handleFullscreen}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    ),
    renderBottomToolbarCustomActions: () => (
      <Typography variant="p" className="flat-table__bottom-toolbar__row-count" sx={{fontSize: "13px"}}>
        {t("modules.flatTable.table.bottomToolbar.rowCount.label", {
          rowCount: getFormattedValue(rowCount, decimalSeparator, 0, "", roundingStrategy, true)
        })}
      </Typography>
    ),
    ...FLAT_TABLE_PROPS,
    muiTableContainerProps: {
      ...FLAT_TABLE_PROPS.muiTableContainerProps,
      ref: tableContainerRef
    },
    muiTableBodyCellProps: {
      className: `material-react-table__table__body__cell ${(tableSettings?.isTableMultiline || false) && "material-react-table__table__body__cell--multiline"}`
    },
    ...(disableInteractions && FLAT_TABLE_NO_INTERACTION_PROPS),
    enableFilters: !disableInteractions && !disableFilters,
    enableSorting: !disableInteractions && !disableSorting,
    enableColumnDragging: !disableColumnOrdering,
    enableColumnOrdering: !disableColumnOrdering
  });

  return (columns || []).length ? (
    <>
      <MaterialReactTable table={table} />
      <SettingsDialog
        isSettingsVisible={isSettingsVisible}
        setSettingsVisibility={setSettingsVisibility}
        tableSettings={tableSettings}
        setChartSettings={setTableSettings}
      />
    </>
  ) : columns ? (
    <Card sx={TABLE_CARD_STYLE}>{t("modules.flatTable.table.loading")}</Card>
  ) : (
    <Card sx={TABLE_CARD_STYLE}>{t("modules.flatTable.table.error")}</Card>
  );
};

const FlatTable = props => (
  <FullscreenProvider>
    <FlatTableComponent {...props} />
  </FullscreenProvider>
);

export default FlatTable;
